/**
 * ASSISTANT — ÉTAPE 1 : LA NUIT, CE QU'ELLE ANNONCE, ET SON PÉRIMÈTRE.
 *
 * ═══ UNE TABLE, PAS UNE PILE DE DOCUMENTS ═══
 *
 * Une nuit ZAP, c'est une synthèse, parfois des compléments « hors ligne », et
 * une trentaine d'annexes (doseurs, tiroirs, TVA, produits…). Seules les
 * PIÈCES D'ARGENT comptent pour la clôture : le rapport principal, ses
 * compléments et la reddition Shotgun. Elles sont additionnées par le serveur
 * (`nightDetail`) et posées en UNE table par point de vente ; les annexes sont
 * des preuves, repliées, consultables d'un clic.
 *
 * ═══ LE PÉRIMÈTRE (décision de l'exploitant, 07/10/2026) ═══
 *
 * L'Accueil et le Vestiaire sont ouverts TOUTES les nuits : ils ne se
 * demandent plus. Seules les salles se déclarent ouvertes ou fermées — et une
 * salle où ZAP a vendu est ouverte, c'est écrit dans le rapport. Une salle
 * sans vente reste une question : « pas répondu » n'est pas « fermé ».
 *
 * ═══ L'ACCUEIL : LA NUIT, ET LES VENTES D'UNE AUTRE NUIT (08/10/2026) ═══
 *
 * Le guichet Shotgun de l'Accueil ne porte que les ventes faites PENDANT la
 * nuit. Les ventes faites ici sur la soirée d'une autre nuit (la caisse n°2
 * restée ouverte sur le 26/09, le 03/10) s'ajoutent en ligne à part : dans
 * l'annoncé une fois RATTACHÉES, hors du total tant qu'elles attendent leur
 * décision — et la ligne le dit. Une décision qui ne couvre qu'une partie des
 * billets (d'autres vendus depuis) compte pour les siens : c'est ce que le
 * serveur ajoute à l'attendu. La soirée d'ici vendue pendant une autre nuit
 * est rappelée sous l'Accueil.
 *
 * ═══ LES ENTRÉES ═══
 *
 * Combien de personnes sont entrées : les billets Shotgun se relèvent seuls
 * (valides, dont scannés, guichet, préventes, exonérations) ; la guestlist,
 * que seule la feuille de l'Accueil connaît, se saisit à l'étape de
 * l'ACCUEIL — à côté de sa caisse (exploitant, 07/10/2026 : « les guestlist à
 * rentrer, il faudrait que ce soit dans la partie Accueil plutôt »). Ici, un
 * résumé en lecture et le chemin vers cette étape (`ResumeEntrees`).
 */

import { useMemo, useState } from 'react'
import { CalendarDays, CheckCircle2, FileText, Lock, Plus, RefreshCw, Store } from 'lucide-react'
import { Combobox } from '../admin/pickers.js'
import { adminMutate, useToast } from '../admin/primitives.js'
import { formatMoney, sommeCents } from './money.js'
import {
  EnCours, Indisponible, heureParis,
  type ClosingApi, type ClosingDetail, type ClosingPosView, type Etat, type PointOfSaleRow,
} from './ClosingCommon.js'
import { jourMois, montantNuit, type NightDetailResponse } from './nights-common.js'
import { lignesHorsNuitDeLEtape } from './guichet-hors-nuit-model.js'
import { VoirSource, type DocVisible } from './SourceViewer.js'
import { ResumeEntrees } from './entrees.js'

/** Les points de vente qui ne ferment jamais : ils ne se demandent pas. */
const TOUJOURS_OUVERTS = new Set(['BILLETTERIE', 'VESTIAIRE'])
export const estToujoursOuvert = (p: Pick<ClosingPosView, 'point_of_sale_kind'>): boolean =>
  TOUJOURS_OUVERTS.has(p.point_of_sale_kind)

/** Un brouillon doit-il être amorcé ? (réponses évidentes manquantes, ou rien de repris) */
export function doitAmorcer(d: ClosingDetail, n: NightDetailResponse): boolean {
  if (d.status !== 'DRAFT') return false
  const vend = (posId: string) => n.by_point_of_sale.some((x) => x.point_of_sale_id === posId && x.announced_cents > 0)
  const reponseEvidente = d.pos.some((p) => p.was_open === null && (estToujoursOuvert(p) || vend(p.point_of_sale_id)))
  const rienRepris = !d.pos.some((p) => p.lines.some((l) => l.source_cents !== null))
  const aReprendre = n.by_point_of_sale.some((x) => x.resolved && x.announced_cents > 0)
    || (n.accueil.settled && (n.accueil.onsite?.facial_cents ?? 0) > 0)
  return reponseEvidente || (rienRepris && aReprendre)
}

interface LigneAnnonce {
  cle: string
  nom: string
  detail: string | null
  pos: ClosingPosView | null
  carte: number | null
  especes: number | null
  offerts: number | null
  annonce: number | null
  avertissement: string | null
}

export function ClosingStepNight(props: {
  api: ClosingApi
  nuit: Etat<NightDetailResponse>
  pieces: DocVisible[]
  amorcage: boolean
  posDispo: PointOfSaleRow[]
  figee: boolean
  /** Aller à l'étape de l'Accueil, où la guestlist se saisit ; `null` s'il n'y en a pas. */
  onAccueil: (() => void) | null
}) {
  const { api, nuit, pieces, amorcage, posDispo, figee, onAccueil } = props
  const d = api.closing
  const toast = useToast()
  const [ajout, setAjout] = useState<string | null>(null)
  const [enCours, setEnCours] = useState<string | null>(null)
  const [reprise, setReprise] = useState(false)
  const peutEditer = api.peut('finance.closure.edit') && !figee
  const n = nuit.etat === 'ok' ? nuit.valeur : null

  /** Ce que la clôture porte DÉJÀ en source, par point de vente. */
  const sourceDe = (posId: string | null): number | null => {
    if (!posId) return null
    const p = d.pos.find((x) => x.point_of_sale_id === posId)
    const l = p ? p.lines.filter((x) => x.source_cents !== null) : []
    return l.length === 0 ? null : sommeCents(l.map((x) => x.source_cents))
  }

  /* La table : les points de vente ZAP, additionnés par le serveur, puis
     l'Accueil tel que le guichet Shotgun le dit. */
  const lignes: LigneAnnonce[] = useMemo(() => {
    if (!n) return []
    const out: LigneAnnonce[] = n.by_point_of_sale.map((p, i) => ({
      cle: p.point_of_sale_id ?? `alias-${i}`,
      nom: p.name,
      detail: p.resolved ? null : 'caisse du rapport sans correspondance',
      pos: p.point_of_sale_id ? d.pos.find((x) => x.point_of_sale_id === p.point_of_sale_id) ?? null : null,
      carte: p.by_method['CB'] ?? 0,
      especes: p.by_method['ESPECES'] ?? 0,
      offerts: p.by_method['OFFERT'] ?? null,
      annonce: p.announced_cents,
      avertissement: p.resolved ? null : 'À rattacher dans les référentiels : ce montant ne rejoint aucune clôture.',
    }))
    const o = n.accueil.onsite
    const accueil = d.pos.find((x) => x.point_of_sale_kind === 'BILLETTERIE') ?? null
    const horsNuit = n.off_night_sales ?? []
    // Seuls les billets SANS décision se décident encore : maintenus ou
    // rattachés, l'avertissement « se décident à l'étape Accueil » était
    // périmé ; une décision partielle (billets vendus depuis) n'en laisse que
    // le reste.
    const vendusAilleurs = horsNuit.filter((c) => c.side === 'SOIREE' && c.status === 'EN_ATTENTE' && c.undecided_tickets > 0)
    // LA SOURCE DÉJÀ POSÉE FAIT FOI. Une pose « par soirée » d'avant le
    // 08/10/2026 contient des ventes d'une autre nuit : le 26/09, 5 880 € posés
    // pour 4 820 € vendus pendant la nuit. Annoncer 4 820 € ici quand l'étape
    // Accueil, le rail et la feuille disent 5 880 € (l'attendu, celui que la
    // finalisation retiendrait) faisait mentir l'une des deux. Sans source
    // posée, ce que la pose posera : les ventes de la nuit.
    const posee = accueil ? sourceDe(accueil.point_of_sale_id) : null
    const sourceDuMoyen = (code: string): number | null => {
      const ls = accueil?.lines.filter((x) => x.payment_method === code && x.source_cents !== null) ?? []
      return ls.length === 0 ? null : sommeCents(ls.map((x) => x.source_cents))
    }
    if ((o && o.facial_cents > 0) || posee !== null) {
      out.push({
        cle: 'accueil-shotgun',
        nom: accueil?.point_of_sale_name ?? 'Accueil',
        detail: posee !== null ? 'guichet Shotgun, source posée dans la clôture' : 'guichet Shotgun, ventes de la nuit',
        pos: accueil,
        carte: posee !== null ? sourceDuMoyen('CB') : o && o.coverage === 'COMPLETE' ? o.by_method.card_present_cents : null,
        especes: posee !== null ? sourceDuMoyen('ESPECES') : o && o.coverage === 'COMPLETE' ? o.by_method.cash_cents : null,
        offerts: null,
        annonce: posee ?? o?.facial_cents ?? null,
        avertissement: posee === null && !n.accueil.settled
          ? 'La billetterie n’a pas encore été relue après la fin de nuit : pas encore reprise.'
          : posee === null && o !== null && o.coverage !== 'COMPLETE' ? 'Une partie du guichet n’a pas de moyen de paiement relevé : à saisir au comptage.'
            : vendusAilleurs.length > 0
              ? vendusAilleurs.map((c) => `${c.undecided_tickets} billet${c.undecided_tickets > 1 ? 's' : ''} de cette soirée, vendu${c.undecided_tickets > 1 ? 's' : ''} pendant la nuit du ${jourMois(c.sale_business_date)}, se décide${c.undecided_tickets > 1 ? 'nt' : ''} à l’étape Accueil.`).join(' ')
              : null,
      })
    }
    // Les ventes hors nuit : ce que chaque décision EN VIGUEUR fait à l'attendu
    // — le montant du serveur, même quand des billets vendus depuis remettent
    // le cas « en attente » (`lignesHorsNuitDeLEtape`).
    for (const l of lignesHorsNuitDeLEtape(horsNuit)) {
      out.push({
        cle: l.cle,
        nom: accueil?.point_of_sale_name ?? 'Accueil',
        detail: l.detail,
        pos: l.accueil ? accueil : null,
        carte: l.carte,
        especes: l.especes,
        offerts: null,
        annonce: l.annonce,
        avertissement: l.avertissement,
      })
    }
    return out
  }, [n, d.pos])

  const totalAnnonce = sommeCents(lignes.map((l) => l.annonce))
  const annexes: DocVisible[] = n
    ? n.documents.filter((x) => x.report_type === 'ZAP_OTHER' && !x.ignored).map((x) => ({
      id: x.id, filename: x.filename, provider: x.provider, report_type: x.report_type,
      business_date: n.business_date, received_at: x.received_at,
    }))
    : []

  /** REPRENDRE LES MONTANTS DE LA NUIT — pour un rapport arrivé après l'ouverture. */
  const reprendre = async () => {
    setReprise(true)
    const r = await adminMutate<{
      error?: string; message?: string
      applied_cents?: number; skipped_cents?: number; shotgun_cents?: number
      signals?: Array<{ detail: string }>; reports?: Array<{ role: string }>
    }>('POST', `/api/v1/finance/closings/${d.id}/prefill-night`, { expected_version: d.version })
    setReprise(false)
    if (r.status >= 200 && r.status < 300 && r.data.error === undefined) {
      const rapports = r.data.reports ?? []
      const guichet = r.data.shotgun_cents ?? 0
      const zap = (r.data.applied_cents ?? 0) - guichet
      const parties = [
        rapports.length > 0 ? `${formatMoney(zap)} repris de ${rapports.length} rapport${rapports.length > 1 ? 's' : ''} ZAP` : null,
        guichet > 0 ? `${formatMoney(guichet)} du guichet Shotgun pour l’Accueil` : null,
      ].filter((x): x is string => x !== null)
      const repris = parties.length > 0 ? parties.join(', ') : 'Rien de nouveau à reprendre'
      const signaux = r.data.signals ?? []
      if (signaux.length > 0) {
        toast('error', `${repris}. Non repris : ${signaux[0]!.detail}${signaux.length > 1 ? ` (et ${signaux.length - 1} autre${signaux.length > 2 ? 's' : ''})` : ''}`)
      } else {
        toast('ok', `${repris}. Le constaté reste à saisir.`)
      }
      api.recharger()
      return
    }
    toast('error', r.data.message ?? 'Les montants de la nuit n’ont pas pu être repris.')
  }

  const absents = useMemo(
    () => posDispo.filter((p) => p.active && !d.pos.some((x) => x.point_of_sale_id === p.id)),
    [posDispo, d.pos],
  )

  const repondre = async (pointOfSaleId: string, wasOpen: boolean | null) => {
    setEnCours(pointOfSaleId)
    await api.ecrire('PATCH', `/api/v1/finance/closings/${d.id}/pos/${pointOfSaleId}`, { was_open: wasOpen })
    setEnCours(null)
  }

  const ajouter = async () => {
    if (!ajout) return
    setEnCours(ajout)
    const ok = await api.ecrire('POST', `/api/v1/finance/closings/${d.id}/pos`, { point_of_sale_id: ajout })
    setEnCours(null)
    if (ok) setAjout(null)
  }

  const salles = d.pos.filter((p) => !estToujoursOuvert(p))
  const fixes = d.pos.filter(estToujoursOuvert)
  const sansReponse = salles.filter((p) => p.was_open === null).length
  const venteDe = (posId: string): number | null =>
    n?.by_point_of_sale.find((x) => x.point_of_sale_id === posId)?.announced_cents ?? null

  return (
    <>
      {/* ═══════════ La nuit ═══════════ */}
      <section className="lc-panel">
        <div className="lc-panel__head">
          <span className="fih-head__icon" aria-hidden="true"><CalendarDays size={17} /></span>
          <h2 className="lc-panel__title">La nuit</h2>
        </div>
        <div className="lc-panel__body fiw-nuit">
          <div className="fiw-nuit__bloc">
            <p className="fi-kpi__label">{d.soirees.length > 1 ? 'Soirées' : 'Soirée'}</p>
            {d.soirees.length === 0 ? (
              <p className="lc-hint">Aucune soirée déclarée : une nuit peut avoir tourné sans être annoncée.</p>
            ) : (
              <ul className="fiw-soirees">
                {d.soirees.map((s) => (
                  <li key={s.id}>
                    <span className="fiw-soirees__nom">{s.name}</span>
                    <span className="fiw-soirees__heure">{heureParis(s.starts_at)}</span>
                    {s.status === 'CANCELLED' ? <span className="lc-pill lc-pill--idle">Annulée</span> : null}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="fiw-nuit__bloc">
            <p className="fi-kpi__label">Rapport ZAP · bars et vestiaire</p>
            {n && n.zap.announced_cents !== null ? (
              <>
                <p className="fiw-nuit__valeur fi-money">{formatMoney(n.zap.announced_cents)}</p>
                <p className="lc-hint">
                  Annoncé hors offerts{n.zap.offers_cents ? ` · ${formatMoney(n.zap.offers_cents)} d’offerts` : ''}
                </p>
              </>
            ) : (
              <p className="lc-hint">{n ? 'Aucun rapport ZAP pour cette nuit.' : 'Lecture en cours'}</p>
            )}
          </div>

          <div className="fiw-nuit__bloc">
            <p className="fi-kpi__label">Accueil · guichet Shotgun</p>
            {n?.accueil.onsite && n.accueil.onsite.facial_cents > 0 ? (
              <>
                <p className="fiw-nuit__valeur fi-money">{formatMoney(n.accueil.onsite.facial_cents)}</p>
                <p className="lc-hint">
                  {n.accueil.onsite.coverage === 'COMPLETE'
                    ? `TPE ${formatMoney(n.accueil.onsite.by_method.card_present_cents)} · espèces ${formatMoney(n.accueil.onsite.by_method.cash_cents)}`
                    : 'Moyens de paiement en partie non relevés'}
                </p>
              </>
            ) : (
              <p className="lc-hint">{n ? 'Aucune vente au guichet.' : 'Lecture en cours'}</p>
            )}
          </div>

        </div>
      </section>

      {/* ═══════════ Ce que la nuit annonce — la SOURCE ═══════════ */}
      <section className="lc-panel">
        <div className="lc-panel__head">
          <span className="fih-head__icon" aria-hidden="true"><FileText size={17} /></span>
          <h2 className="lc-panel__title">Ce que la nuit annonce</h2>
          <span className="lc-panel__spacer" />
          {peutEditer && n && lignes.length > 0 ? (
            <button className="lc-btn lc-btn--sm" disabled={reprise || amorcage} onClick={() => void reprendre()}>
              <RefreshCw size={14} aria-hidden="true" />{reprise || amorcage ? 'Reprise en cours' : 'Reprendre les montants de la nuit'}
            </button>
          ) : null}
        </div>
        <div className="lc-panel__body">
          {nuit.etat === 'chargement' ? <EnCours quoi="de la nuit" /> : null}
          {nuit.etat === 'absent' ? <Indisponible titre="La nuit n’a pas pu être lue" message={nuit.message} /> : null}

          {n ? (
            <>
              <div className="fiw-pieces">
                <span className="fi-kpi__label">Pièces d’argent</span>
                {pieces.length === 0 ? (
                  <span className="lc-hint">Aucun rapport ZAP ni reddition Shotgun pour cette nuit.</span>
                ) : (
                  <ul className="fiw-pieces__liste">
                    {pieces.map((p) => (
                      <li key={p.id}>
                        <VoirSource doc={p} liste={pieces} className="fiw-piece">
                          <FileText size={14} aria-hidden="true" />
                          <span className="fiw-piece__lib">{p.libelle ?? p.filename}</span>
                          {p.received_at ? <span className="fiw-piece__heure">{heureParis(p.received_at)}</span> : null}
                        </VoirSource>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {lignes.length === 0 ? (
                <p className="lc-hint fiw-intro">
                  Rien n’est annoncé pour cette nuit. Les montants seront constatés au terrain, sans source à
                  confronter — et l’écart s’affichera comme tel.
                </p>
              ) : (
                <div className="fi-tablewrap">
                  <table className="fi-table fi-table--chiffres">
                    <colgroup>
                      <col />
                      <col className="fi-col-montant" />
                      <col className="fi-col-montant" />
                      <col className="fi-col-montant" />
                      <col className="fi-col-montant" />
                      <col className="fi-col-etat" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th scope="col">Point de vente</th>
                        <th scope="col" className="fi-money">Carte</th>
                        <th scope="col" className="fi-money">Espèces</th>
                        <th scope="col" className="fi-money">Offerts</th>
                        <th scope="col" className="fi-money">Annoncé</th>
                        <th scope="col">Dans la clôture</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lignes.map((l) => {
                        const enSource = sourceDe(l.pos?.point_of_sale_id ?? null)
                        return (
                          <tr key={l.cle}>
                            <th scope="row">
                              <span className="fi-cell-titre">{l.nom}</span>
                              {l.detail ? <span className="fi-cell-sous">{l.detail}</span> : null}
                              {l.avertissement ? <span className="fi-cell-alerte">{l.avertissement}</span> : null}
                            </th>
                            <td className="fi-money">{montantNuit(l.carte)}</td>
                            <td className="fi-money">{montantNuit(l.especes)}</td>
                            <td className="fi-money">{l.offerts === null ? '—' : montantNuit(l.offerts)}</td>
                            <td className="fi-money fi-money--fort">{montantNuit(l.annonce)}</td>
                            <td>
                              {enSource !== null ? (
                                <span className="lc-pill lc-pill--ok"><CheckCircle2 size={13} aria-hidden="true" /> En source</span>
                              ) : l.pos === null ? (
                                <span className="lc-hint">—</span>
                              ) : l.pos.was_open === false ? (
                                <span className="lc-pill lc-pill--idle">Déclaré fermé</span>
                              ) : (
                                <span className="lc-pill lc-pill--warning">Pas encore repris</span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td>Total annoncé</td>
                        <td colSpan={3} />
                        <td className="fi-money">{formatMoney(totalAnnonce)}</td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              <p className="lc-hint fiw-note">
                Une SOURCE : ce que les rapports annoncent (principal et compléments additionnés, offerts à part ;
                l’Accueil vient du guichet Shotgun). Le comptage et les terminaux sont des CONSTATÉS, saisis point de
                vente par point de vente ; le RETENU s’arrête à la fin. Une source ne s’écrase jamais : un montant
                déjà repris reste tel quel, même si un rapport arrive ou est relu.
              </p>

              {annexes.length > 0 ? (
                <details className="fiw-annexes">
                  <summary>
                    {annexes.length} annexe{annexes.length > 1 ? 's' : ''} ZAP — doseurs, tiroirs, produits, TVA, cartes.
                    Des preuves, rien à faire.
                  </summary>
                  <ul>
                    {annexes.map((a) => (
                      <li key={a.id}><VoirSource doc={a} liste={annexes} /></li>
                    ))}
                  </ul>
                </details>
              ) : null}
            </>
          ) : null}
        </div>
      </section>

      {/* ═══════════ Les entrées — en lecture ; la guestlist se saisit à l'Accueil ═══════════ */}
      <ResumeEntrees e={d.entries ?? null} onAccueil={onAccueil} figee={figee} />

      {/* ═══════════ Le périmètre ═══════════ */}
      <section className="lc-panel">
        <div className="lc-panel__head">
          <span className="fih-head__icon" aria-hidden="true"><Store size={17} /></span>
          <h2 className="lc-panel__title">Ce qui était ouvert cette nuit-là</h2>
          <span className="lc-panel__spacer" />
          {sansReponse > 0 ? (
            <span className="lc-pill lc-pill--warning">{sansReponse} salle{sansReponse > 1 ? 's' : ''} sans réponse</span>
          ) : <span className="lc-pill lc-pill--ok">Périmètre confirmé</span>}
        </div>
        <div className="lc-panel__body">
          <ul className="fiw-perimetre">
            {salles.map((p) => {
              const occupe = enCours === p.point_of_sale_id
              const vente = venteDe(p.point_of_sale_id)
              return (
                <li key={p.id} className={`fiw-pos${p.was_open === null ? ' fiw-pos--sans' : ''}`}>
                  <div className="fiw-pos__ident">
                    <span className="fiw-pos__nom">{p.point_of_sale_name}</span>
                    <span className="lc-hint">
                      {vente !== null && vente > 0
                        ? `ZAP y annonce ${formatMoney(vente)} : la salle était ouverte.`
                        : 'Aucune vente dans les rapports de la nuit.'}
                    </span>
                  </div>
                  <div className="fiw-pos__reponse">
                    {peutEditer ? (
                      <div className="lc-segment lc-segment--sm" role="group" aria-label={`${p.point_of_sale_name} — était-il ouvert ?`}>
                        <button type="button" className={`lc-segment__opt${p.was_open === true ? ' lc-segment__opt--on' : ''}`}
                          disabled={occupe} onClick={() => void repondre(p.point_of_sale_id, true)}>Ouvert</button>
                        <button type="button" className={`lc-segment__opt${p.was_open === false ? ' lc-segment__opt--on' : ''}`}
                          disabled={occupe} onClick={() => void repondre(p.point_of_sale_id, false)}>Fermé</button>
                      </div>
                    ) : (
                      <span className={p.was_open === null ? 'lc-pill lc-pill--warning' : p.was_open ? 'lc-pill lc-pill--ok' : 'lc-pill lc-pill--idle'}>
                        {p.was_open === null ? 'Sans réponse' : p.was_open ? 'Ouvert' : 'Fermé'}
                      </span>
                    )}
                  </div>
                </li>
              )
            })}
            {fixes.map((p) => (
              <li key={p.id} className="fiw-pos fiw-pos--fixe">
                <div className="fiw-pos__ident">
                  <span className="fiw-pos__nom">{p.point_of_sale_name}</span>
                  <span className="lc-hint">Ouvert toutes les nuits</span>
                </div>
                <div className="fiw-pos__reponse">
                  <span className="lc-pill lc-pill--ok"><Lock size={12} aria-hidden="true" /> Ouvert</span>
                </div>
              </li>
            ))}
          </ul>

          {peutEditer && absents.length > 0 ? (
            <details className="fiw-ajout">
              <summary>Ajouter un point de vente non prévu</summary>
              <div className="fiw-ajout__ligne">
                <Combobox
                  value={ajout}
                  items={absents.map((p) => ({ value: p.id, label: p.name, hint: p.location_name }))}
                  onChange={setAjout}
                  placeholder="Choisir un point de vente"
                  ariaLabel="Point de vente à ajouter"
                  clearable
                />
                <button className="lc-btn" disabled={!ajout || enCours !== null} onClick={() => void ajouter()}>
                  <Plus size={16} aria-hidden="true" />Ajouter
                </button>
              </div>
            </details>
          ) : null}
        </div>
      </section>
    </>
  )
}
